import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, getProfileAssetUrl } from '../utils/api';
import { getCpRawatLogoUrl } from '../utils/studentCardUrl';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { GraduationCap, Phone, Mail, MapPin, User } from 'lucide-react';

const publicApi = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

const PublicStudentCard = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    publicApi
      .get(`public/student-card/${id}`)
      .then((res) => {
        setData(res.data?.data ?? null);
        setError(null);
      })
      .catch((err) => {
        setData(null);
        setError(err.response?.data?.message || 'Student not found');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <p className="text-muted-foreground mb-4">{error || 'Student not found'}</p>
        <Link to="/login" className="text-orange-500 hover:underline text-sm">Go to login</Link>
      </div>
    );
  }

  const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ') || 'Student';
  const avatarUrl = getProfileAssetUrl(data.avatar);
  const classLabel = [data.class_name, data.section_name].filter(Boolean).join(' - ');

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-background dark:from-orange-950/20 dark:to-background py-8 px-4">
      <div className="max-w-md mx-auto space-y-4">
        <Card className="border-orange-200 dark:border-orange-900/50 overflow-hidden">
          <div className="h-2 bg-orange-500" />
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-lg text-foreground">{data.school_name || 'School'}</CardTitle>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Student ID — verified</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              <div className="h-24 w-24 rounded-full border-4 border-orange-500 overflow-hidden bg-muted">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={fullName} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-2xl font-bold text-orange-600">
                    {(data.first_name?.[0] || '') + (data.last_name?.[0] || '')}
                  </div>
                )}
              </div>
              <h2 className="text-xl font-bold text-foreground">{fullName}</h2>
              <p className="text-sm text-muted-foreground font-mono">{data.admission_no}</p>
            </div>
            <dl className="space-y-2 text-sm">
              {data.roll_no && (
                <div className="flex justify-between border-b border-border py-1.5">
                  <dt className="text-muted-foreground">Student ID</dt>
                  <dd className="text-foreground font-medium">{data.roll_no}</dd>
                </div>
              )}
              {classLabel && (
                <div className="flex justify-between border-b border-border py-1.5">
                  <dt className="text-muted-foreground flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> Class</dt>
                  <dd className="text-foreground font-medium">{classLabel}</dd>
                </div>
              )}
              {data.guardian_name && (
                <div className="flex justify-between border-b border-border py-1.5">
                  <dt className="text-muted-foreground flex items-center gap-1"><User className="w-3.5 h-3.5" /> Guardian</dt>
                  <dd className="text-foreground font-medium text-right">{data.guardian_name}</dd>
                </div>
              )}
              {data.guardian_phone && (
                <div className="flex justify-between border-b border-border py-1.5">
                  <dt className="text-muted-foreground flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Emergency</dt>
                  <dd className="text-foreground font-medium">{data.guardian_phone}</dd>
                </div>
              )}
              {data.phone && (
                <div className="flex justify-between border-b border-border py-1.5">
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd className="text-foreground">{data.phone}</dd>
                </div>
              )}
            </dl>
            {(data.school_address || data.school_phone) && (
              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border">
                {data.school_address && (
                  <p className="flex items-start gap-1"><MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />{data.school_address}</p>
                )}
                {data.school_phone && <p className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{data.school_phone}</p>}
                {data.school_email && <p className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{data.school_email}</p>}
              </div>
            )}
          </CardContent>
          <div className="bg-orange-500 text-white text-center text-xs py-2 px-3">Official student record</div>
        </Card>
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <span>Managed by</span>
          <img src={getCpRawatLogoUrl()} alt="CP Rawat Softwares" className="h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="font-medium text-foreground">CP Rawat Softwares</span>
        </div>
      </div>
    </div>
  );
};

export default PublicStudentCard;
